<template>
  <div class="sci-view-container">
    <!-- Top Glassmorphic Navigation -->
    <header class="sci-header">
      <div class="sci-header-left">
        <button class="sci-btn-back" @click="home">
          <i class="fas fa-arrow-left"></i>
          <span>{{ $t('gestionale') || 'Gestionale' }}</span>
        </button>
        <div class="sci-title-group">
          <h1 class="sci-title">
            <i class="fas fa-print me-2 text-primary"></i>
            {{ $t('stampanti') || 'Configurazione Stampanti Comande & Reparti' }}
          </h1>
          <p class="sci-subtitle">Configura stampanti termiche IP/Ethernet e assegna i reparti di comanda</p>
        </div>
      </div>
      <div class="sci-header-right">
        <button class="sci-btn sci-btn-primary" @click="addPrinters">
          <i class="fas fa-plus me-1"></i>
          <span>Nuova Stampante</span>
        </button>
      </div>
    </header>

    <!-- Floating Feedback Toast -->
    <transition name="fade">
      <div class="sci-floating-toast" v-if="feedbackMsg">
        <i class="fas fa-check-circle text-success me-2"></i>
        <span>{{ feedbackMsg }}</span>
      </div>
    </transition>

    <!-- Main Spatial Bento Grid -->
    <main class="sci-main-content">
      <!-- Info KPI Header -->
      <section class="kpi-bento-grid mb-4">
        <div class="sci-card kpi-card">
          <div class="kpi-icon-box printer-icon">
            <i class="fas fa-print"></i>
          </div>
          <div class="kpi-info">
            <span class="kpi-label">Stampanti Configurate</span>
            <span class="kpi-number">{{ printers.length }}</span>
            <span class="kpi-subtext text-muted">Dispositivi di stampa termica</span>
          </div>
        </div>

        <div class="sci-card kpi-card">
          <div class="kpi-icon-box station-icon">
            <i class="fas fa-utensils"></i>
          </div>
          <div class="kpi-info">
            <span class="kpi-label">Reparti Disponibili</span>
            <span class="kpi-number">{{ postazioni.length }}</span>
            <span class="kpi-subtext text-muted">Centri di produzione abilitati</span>
          </div>
        </div>

        <div class="sci-card kpi-card">
          <div class="kpi-icon-box covered-icon">
            <i class="fas fa-check-double"></i>
          </div>
          <div class="kpi-info">
            <span class="kpi-label">Reparti Coperti</span>
            <span class="kpi-number text-success">{{ uniqueCoveredStations.length }}</span>
            <span class="kpi-subtext text-muted">Con almeno una stampante associata</span>
          </div>
        </div>
      </section>

      <!-- Printers List / Cards -->
      <section class="printers-grid">
        <div 
          v-for="(printer, i) in printers" 
          :key="printer.id || i" 
          class="sci-card printer-card"
        >
          <!-- Card Header -->
          <div class="printer-card-header">
            <div class="printer-badge-group">
              <div class="printer-avatar">
                <i class="fas fa-print"></i>
              </div>
              <div class="printer-title-wrap">
                <h3 class="printer-name">
                  {{ printer.nome || 'Nuova Stampante (Senza Nome)' }}
                </h3>
                <span class="printer-network-label font-monospace" v-if="printer.ip">
                  {{ printer.ip }}:{{ printer.porta || '9100' }}
                </span>
              </div>
            </div>

            <button 
              class="btn-delete-printer" 
              title="Elimina Stampante" 
              @click="action(i, printer.id, printer.nome, printer.ip, printer.porta, printer.postazioni, false)"
            >
              <i class="fas fa-trash-alt"></i>
            </button>
          </div>

          <!-- Card Form Body -->
          <div class="printer-form-body">
            <div class="form-row-grid">
              <!-- Name Input -->
              <div class="sci-input-group">
                <label class="sci-label">Nome Dispositivo / Reparto</label>
                <div class="sci-input-wrap">
                  <i class="fas fa-tag input-icon"></i>
                  <input 
                    type="text" 
                    class="sci-input" 
                    placeholder="Es. Stampante Pizzeria" 
                    v-model="printer.nome"
                  />
                </div>
              </div>

              <!-- IP Address -->
              <div class="sci-input-group">
                <label class="sci-label">Indirizzo IP Locale</label>
                <div class="sci-input-wrap">
                  <i class="fas fa-network-wired input-icon"></i>
                  <input 
                    type="text" 
                    class="sci-input font-monospace" 
                    placeholder="Es. 192.168.1.200" 
                    v-model="printer.ip"
                  />
                </div>
              </div>

              <!-- Port -->
              <div class="sci-input-group" style="max-width: 140px;">
                <label class="sci-label">Porta RAW</label>
                <div class="sci-input-wrap">
                  <i class="fas fa-plug input-icon"></i>
                  <input 
                    type="text" 
                    class="sci-input font-monospace" 
                    placeholder="9100" 
                    v-model="printer.porta"
                  />
                </div>
              </div>
            </div>

            <!-- Postazioni Multiselect -->
            <div class="sci-input-group mt-3">
              <label class="sci-label">Reparti & Categorie Assegnate a Questa Stampante</label>
              <div class="multiselect-wrap">
                <Multiselect
                  v-model="printer.postazioni"
                  :options="postazioni"
                  mode="tags"
                  :close-on-select="false"
                  placeholder="Seleziona i reparti (es. Primi, Pizze, Bevande)..."
                  class="sci-multiselect"
                />
              </div>
            </div>
          </div>

          <!-- Card Footer Action -->
          <div class="printer-card-footer">
            <span class="station-count-pill" v-if="printer.postazioni && printer.postazioni.length">
              <i class="fas fa-check me-1"></i> {{ printer.postazioni.length }} reparti collegati
            </span>
            <span class="station-count-pill pill-warning" v-else>
              <i class="fas fa-exclamation-triangle me-1"></i> Nessun reparto assegnato
            </span>

            <button 
              class="sci-btn sci-btn-success ms-auto" 
              @click="action(i, printer.id, printer.nome, printer.ip, printer.porta, printer.postazioni, true)"
              :disabled="!printer.nome || !printer.ip"
            >
              <i class="fas fa-save me-1"></i>
              <span>Salva Configurazione</span>
            </button>
          </div>
        </div>

        <!-- Empty State -->
        <div class="sci-card empty-card" v-if="printers.length === 0">
          <div class="empty-icon-halo">
            <i class="fas fa-print"></i>
          </div>
          <h3 class="empty-title">Nessuna stampante configurata</h3>
          <p class="empty-desc">
            Aggiungi la prima stampante di reparto (cucina, pizzeria, bar o cassa) specificando il suo indirizzo IP di rete.
          </p>
          <button class="sci-btn sci-btn-primary mt-3" @click="addPrinters">
            <i class="fas fa-plus me-1"></i> Aggiungi Stampante Ora
          </button>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
import Multiselect from '@vueform/multiselect'
import { store } from '../store/store.js'

export default {
  name: 'Stampanti',
  components: {
    Multiselect
  },
  data() {
    return {
      postazioni: [
        "antipasti", "primi", "secondi", "contorni", "pizza",
        "sushi", "dolci", "frutta", "caffe", "bevande",
        "amari", "cassa", "asporto", "poke"
      ],
      printers: [],
      feedbackMsg: '',
      store
    }
  },
  computed: {
    uniqueCoveredStations() {
      const set = new Set();
      this.printers.forEach(p => {
        if (Array.isArray(p.postazioni)) {
          p.postazioni.forEach(st => set.add(st));
        }
      });
      return Array.from(set);
    }
  },
  methods: {
    getToken() {
      try {
        if (!localStorage.token) return localStorage.getItem('token') || '';
        const parsed = JSON.parse(localStorage.token);
        return typeof parsed === 'string' ? parsed : (localStorage.getItem('token') || '');
      } catch (e) {
        return localStorage.getItem('token') || '';
      }
    },
    home() {
      this.$router.push({ name: "Gestionale" });
    },
    addPrinters() {
      this.printers.unshift({ nome: '', ip: '', porta: '9100', postazioni: [] });
      this.showToast('Nuova scheda stampante inserita in cima');
    },
    displayPrinters() {
      const token = this.getToken();
      const authHeader = `Bearer ${token}`;

      this.axios.post(this.store.path + 'displayPrinter', {
        headers: {
          authorization: authHeader
        }
      }, {
        headers: {
          authorization: authHeader
        }
      }).then((response) => {
        if (response.data && response.data.messaggio) {
          const loaded = [];
          response.data.messaggio.forEach(element => {
            loaded.push({
              id: element._id,
              nome: element.nome,
              ip: element.ip,
              porta: element.porta,
              postazioni: Array.isArray(element.postazioni) ? element.postazioni : []
            });
          });
          this.printers = loaded;
        }
      }).catch(err => {
        console.error("Errore caricamento stampanti:", err);
      });
    },
    action(ind, id, nome, ip, porta, postazioni, save) {
      if (!save) {
        if (!confirm(`Sei sicuro di voler eliminare la stampante "${nome || 'Senza Nome'}"?`)) {
          return;
        }
      }

      var path = save ? 'creaPrinter' : 'deletePrinter';
      if (!save) {
        this.printers.splice(ind, 1);
      }

      const token = this.getToken();
      const authHeader = `Bearer ${token}`;

      this.axios.post(this.store.path + path, {
        id: id,
        nome: nome,
        ip: ip,
        porta: porta,
        postazioni: postazioni,
        headers: {
          authorization: authHeader
        }
      }, {
        headers: {
          authorization: authHeader
        }
      }).then((response) => {
        if (response.data && response.data.messaggio) {
          this.showToast(save ? 'Configurazione stampante salvata' : 'Stampante eliminata con successo');
          if (save) {
            this.displayPrinters();
          }
        } else {
          this.showToast('Operazione completata');
        }
      }).catch(err => {
        console.error("Errore salvataggio stampante:", err);
        alert('Si è verificato un errore durante l\'operazione');
      });
    },
    showToast(msg) {
      this.feedbackMsg = msg;
      setTimeout(() => {
        if (this.feedbackMsg === msg) this.feedbackMsg = '';
      }, 2500);
    }
  },
  mounted() {
    this.displayPrinters();
  }
}
</script>

<style src="@vueform/multiselect/themes/default.css"></style>

<style scoped>
.sci-view-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: var(--sci-bg, #f8fafc);
  color: var(--sci-text-main, #0f172a);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  padding-bottom: 60px;
}

/* Header */
.sci-header {
  position: sticky;
  top: 0;
  z-index: 100;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 28px;
  background: rgba(255, 255, 255, 0.88);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid var(--sci-border, #e2e8f0);
}

.sci-header-left {
  display: flex;
  align-items: center;
  gap: 18px;
}

.sci-btn-back {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  padding: 0 16px;
  background: #ffffff;
  border: 1px solid var(--sci-border, #e2e8f0);
  border-radius: 12px;
  color: var(--sci-text-muted, #475569);
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.22, 1, 0.36, 1);
}

.sci-btn-back:hover {
  background: #f1f5f9;
  color: var(--sci-text-main, #0f172a);
  border-color: #cbd5e1;
}

.sci-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
}

.sci-subtitle {
  margin: 2px 0 0 0;
  font-size: 0.82rem;
  color: var(--sci-text-muted, #475569);
}

.sci-btn-primary {
  display: inline-flex;
  align-items: center;
  min-height: 44px;
  padding: 0 20px;
  background: var(--sci-primary, #4f46e5);
  color: #ffffff;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}

.sci-btn-primary:hover {
  background: #4338ca;
}

.sci-btn-success {
  display: inline-flex;
  align-items: center;
  min-height: 40px;
  padding: 0 18px;
  background: #059669;
  color: #ffffff;
  border: none;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}

.sci-btn-success:hover:not(:disabled) {
  background: #047857;
}

.sci-btn-success:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.sci-main-content {
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
  padding: 24px 20px;
}

/* Card */
.sci-card {
  background: #ffffff;
  border: 1px solid var(--sci-border, #e2e8f0);
  border-radius: 18px;
  box-shadow: 0 4px 6px -1px rgba(15, 23, 42, 0.05);
  padding: 22px;
}

/* KPI Grid */
.kpi-bento-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 16px;
}

.kpi-card {
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 20px;
}

.kpi-icon-box {
  width: 52px;
  height: 52px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.35rem;
  flex-shrink: 0;
}

.printer-icon { background: #eef2ff; color: #4f46e5; }
.station-icon { background: #fff7ed; color: #ea580c; }
.covered-icon { background: #ecfdf5; color: #059669; }

.kpi-info {
  display: flex;
  flex-direction: column;
}

.kpi-label {
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  color: var(--sci-text-muted, #475569);
  letter-spacing: 0.4px;
}

.kpi-number {
  font-size: 1.45rem;
  font-weight: 800;
  margin: 3px 0 2px 0;
}

.kpi-subtext {
  font-size: 0.78rem;
}

/* Printers Grid */
.printers-grid {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.printer-card {
  display: flex;
  flex-direction: column;
  gap: 18px;
  transition: transform 0.2s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.2s;
}

.printer-card:hover {
  box-shadow: 0 10px 15px -3px rgba(15, 23, 42, 0.08);
}

.printer-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #f1f5f9;
  padding-bottom: 14px;
}

.printer-badge-group {
  display: flex;
  align-items: center;
  gap: 14px;
}

.printer-avatar {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: #f1f5f9;
  color: var(--sci-primary, #4f46e5);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
}

.printer-title-wrap {
  display: flex;
  flex-direction: column;
}

.printer-name {
  margin: 0;
  font-size: 1.08rem;
  font-weight: 700;
}

.printer-network-label {
  font-size: 0.8rem;
  color: var(--sci-text-muted, #475569);
}

.btn-delete-printer {
  width: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  border: 1px solid #fee2e2;
  background: #fff5f5;
  color: #ef4444;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-delete-printer:hover {
  background: #ef4444;
  color: #ffffff;
  border-color: #ef4444;
}

.form-row-grid {
  display: grid;
  grid-template-columns: 1fr 1fr auto;
  gap: 16px;
}

@media (max-width: 768px) {
  .form-row-grid {
    grid-template-columns: 1fr;
  }
}

.sci-input-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.sci-label {
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--sci-text-muted, #475569);
}

.sci-input-wrap {
  position: relative;
  display: flex;
  align-items: center;
}

.input-icon {
  position: absolute;
  left: 14px;
  color: #94a3b8;
  font-size: 0.9rem;
}

.sci-input {
  width: 100%;
  height: 42px;
  padding: 0 14px 0 38px;
  border: 1px solid var(--sci-border, #e2e8f0);
  border-radius: 10px;
  font-size: 0.9rem;
  color: var(--sci-text-main, #0f172a);
  background: #ffffff;
  outline: none;
  transition: border-color 0.2s;
}

.sci-input:focus {
  border-color: var(--sci-primary, #4f46e5);
  box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
}

.printer-card-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-top: 1px solid #f1f5f9;
  padding-top: 14px;
}

.station-count-pill {
  font-size: 0.8rem;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 20px;
  background: #ecfdf5;
  color: #065f46;
}

.station-count-pill.pill-warning {
  background: #fffbeb;
  color: #b45309;
}

/* Empty State */
.empty-card {
  text-align: center;
  padding: 60px 20px;
}

.empty-icon-halo {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: #eef2ff;
  color: var(--sci-primary, #4f46e5);
  font-size: 1.8rem;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px auto;
}

.empty-title {
  font-size: 1.2rem;
  font-weight: 700;
  margin-bottom: 6px;
}

.empty-desc {
  font-size: 0.88rem;
  color: var(--sci-text-muted, #475569);
  max-width: 480px;
  margin: 0 auto;
}

/* Floating Toast */
.sci-floating-toast {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 1000;
  display: flex;
  align-items: center;
  background: #0f172a;
  color: #ffffff;
  padding: 12px 20px;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  font-weight: 600;
  font-size: 0.9rem;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}
</style>
